import { DATA_TYPE } from '../config/TypeConfig';
import logger from './logger';

/**
 * 网站工具类
 * 统一管理网站相关的操作逻辑，包括添加、排序、验证等
 */
class WebsiteUtils {
  /**
   * 判断是否为热搜卡片
   * @param {Object} website 网站数据
   * @returns {boolean} 是否为热搜卡片
   */
  static isHotSearchCard(website) {
    return website && website.dataType === DATA_TYPE.HOT_SEARCH;
  }

  /**
   * 判断是否为每日英语卡片
   * @param {Object} website 网站数据
   * @returns {boolean} 是否为每日英语卡片
   */
  static isDailyEnglishCard(website) {
    return website && website.dataType === DATA_TYPE.DAILY_ENGLISH;
  }

  /**
   * 根据网站类型确定插入位置
   * @param {Array} existingWebsites 现有网站数组
   * @param {Object} newWebsite 新网站数据
   * @returns {Array} 更新后的网站数组
   */
  static insertWebsiteByType(existingWebsites = [], newWebsite) {
    if (!newWebsite) {
      logger.warn('[WebsiteUtils] 新网站数据为空，返回原数组');
      return existingWebsites;
    }

    // 如果是热搜卡片或每日英语卡片，插入到第一个位置
    if (this.isHotSearchCard(newWebsite) || this.isDailyEnglishCard(newWebsite)) {
      logger.log('[WebsiteUtils] 检测到特殊卡片，插入到第一个位置');
      return [newWebsite, ...existingWebsites];
    }

    // 普通网站追加到末尾
    logger.log('[WebsiteUtils] 普通网站，追加到末尾');
    return [...existingWebsites, newWebsite];
  }

  /**
   * 批量添加网站，保持类型顺序
   * @param {Array} existingWebsites 现有网站数组
   * @param {Array} newWebsites 新网站数组
   * @returns {Array} 更新后的网站数组
   */
  static batchInsertWebsites(existingWebsites = [], newWebsites = []) {
    if (!Array.isArray(newWebsites) || newWebsites.length === 0) {
      return existingWebsites;
    }

    let result = [...existingWebsites];
    
    newWebsites.forEach(website => {
      result = this.insertWebsiteByType(result, website);
    });

    logger.log(`[WebsiteUtils] 批量添加 ${newWebsites.length} 个网站完成`);
    return result;
  }

  /**
   * 验证网站数据的完整性
   * @param {Object} website 网站数据
   * @returns {Object} 验证结果 { isValid: boolean, errors: Array }
   */
  static validateWebsite(website) {
    const errors = [];
    
    if (!website) {
      errors.push('网站数据不能为空');
      return { isValid: false, errors };
    }

    if (!website.name || typeof website.name !== 'string' || website.name.trim() === '') {
      errors.push('网站名称不能为空');
    }

    if (!website.url || typeof website.url !== 'string' || website.url.trim() === '') {
      errors.push('网站URL不能为空');
    }

    // 验证URL格式
    if (website.url && !this.isValidUrl(website.url)) {
      errors.push('网站URL格式不正确');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * 验证URL格式
   * @param {string} url URL字符串
   * @returns {boolean} 是否为有效URL
   */
  static isValidUrl(url) {
    try {
      new URL(url);
      return true;
    } catch {
      // 如果不是完整URL，检查是否为相对路径或简单域名
      const urlPattern = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;
      return urlPattern.test(url);
    }
  }

  /**
   * 生成网站的唯一标识符
   * @param {Object} website 网站数据
   * @returns {string} 唯一标识符
   */
  static generateWebsiteId(website) {
    if (website.id) {
      return website.id;
    }
    
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 10);
    const name = (website.name || '').substring(0, 10);
    
    return `${name}_${timestamp}_${random}`;
  }

  /**
   * 标准化网站数据
   * @param {Object} website 原始网站数据
   * @returns {Object} 标准化后的网站数据
   */
  static normalizeWebsite(website) {
    if (!website) return null;

    const normalized = { ...website };

    // 确保URL格式正确
    if (normalized.url && !normalized.url.startsWith('http://') && !normalized.url.startsWith('https://')) {
      normalized.url = 'https://' + normalized.url;
    }

    // 确保有唯一ID
    if (!normalized.id) {
      normalized.id = this.generateWebsiteId(normalized);
    }

    // 确保dataType字段存在
    if (normalized.dataType === undefined) {
      normalized.dataType = DATA_TYPE.NAVIGATION;
    }

    // 确保preferredIconType字段存在
    if (normalized.preferredIconType === undefined) {
      normalized.preferredIconType = '2'; // 默认使用文本图标
    }

    return normalized;
  }

  /**
   * 获取网站类型描述
   * @param {Object} website 网站数据
   * @returns {string} 网站类型描述
   */
  static getWebsiteTypeDescription(website) {
    if (this.isHotSearchCard(website)) {
      return '热搜卡片';
    }
    if (this.isDailyEnglishCard(website)) {
      return '每日英语卡片';
    }
    return '普通网站';
  }

  /**
   * 记录网站操作日志
   * @param {string} action 操作类型
   * @param {Object} website 网站数据
   * @param {string} navId 导航ID
   */
  static logWebsiteAction(action, website, navId) {
    const typeDesc = this.getWebsiteTypeDescription(website);
    logger.log(`[WebsiteUtils] ${action} ${typeDesc}: ${website.name || '未知'} (导航: ${navId})`);
  }
}

export default WebsiteUtils; 